import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import { Button, Input, Select, Modal, Toast } from '../components/UI';
import { migrantsAPI, youthAPI } from '../services/api';

const MIG_STATUSES = [
  { value: 'ketgan', label: 'Ketgan', color: '#ef4444', bg: '#fef2f2', icon: 'plane' },
  { value: 'chetda', label: 'Chetda', color: '#f59e0b', bg: '#fffbeb', icon: 'building' },
  { value: 'qaytgan', label: 'Qaytgan', color: '#22c55e', bg: '#f0fdf4', icon: 'home' },
];
const PROB_LEVELS = [
  { value: 'yuqori', label: 'Yuqori', color: '#22c55e', pct: 80 },
  { value: "o'rta", label: "O'rta", color: '#f59e0b', pct: 50 },
  { value: 'past', label: 'Past', color: '#ef4444', pct: 20 },
];
const REF_TYPES = [
  { value: 'ish', label: 'Ishga joylashish', icon: 'briefcase', color: '#3b82f6' },
  { value: 'kurs', label: 'Kasb-hunar kursi', icon: 'graduation', color: '#8b5cf6' },
  { value: 'tadbirkorlik', label: 'Tadbirkorlik', icon: 'store', color: '#ea580c' },
  { value: 'ijtimoiy', label: 'Ijtimoiy yordam', icon: 'heart', color: '#ec4899' },
];
const REF_STATUSES = [
  { value: 'yangi', label: 'Yangi', color: '#6366f1', bg: '#eef2ff' },
  { value: 'jarayonda', label: 'Jarayonda', color: '#f59e0b', bg: '#fffbeb' },
  { value: 'ishga_joylashdi', label: 'Ishga joylashdi', color: '#22c55e', bg: '#f0fdf4' },
  { value: 'rad_etildi', label: 'Rad etildi', color: '#ef4444', bg: '#fef2f2' },
];
const getMS = v => MIG_STATUSES.find(s => s.value === v) || { label: v, color: '#64748b', bg: '#f8fafc', icon: 'users' };
const getRT = v => REF_TYPES.find(t => t.value === v) || REF_TYPES[0];
const getRS = v => REF_STATUSES.find(s => s.value === v) || REF_STATUSES[0];

const FLAG_MAP = { 'Rossiya': '🇷🇺', 'Turkiya': '🇹🇷', "Qozog'iston": '🇰🇿', 'Koreya': '🇰🇷', 'BAA': '🇦🇪', 'Polsha': '🇵🇱', 'Germaniya': '🇩🇪', 'AQSH': '🇺🇸' };
const getFlag = c => FLAG_MAP[c] || '🌍';

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

export default function MigrantsPage() {
  const user = getUser();
  const isAdmin = user?.role === 'viloyat_admin' || user?.role === 'tuman_admin';
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [migrants, setMigrants] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [migStatusF, setMigStatusF] = useState('');
  const [countryF, setCountryF] = useState('');
  const [probF, setProbF] = useState('');
  const [search, setSearch] = useState('');
  const [refFormOpen, setRefFormOpen] = useState(false);
  const [viewRef, setViewRef] = useState(null);
  const [deleteRefId, setDeleteRefId] = useState(null);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const mp = {};
      if (migStatusF) mp.mig_status = migStatusF;
      if (countryF) mp.country = countryF;
      if (probF) mp.probability = probF;
      if (search) mp.search = search;
      const [sR, mR, rR] = await Promise.all([
        migrantsAPI.stats(),
        migrantsAPI.list(mp),
        migrantsAPI.referrals.list(),
      ]);
      setStats(sR.data);
      setMigrants(mR.data);
      setReferrals(rR.data);
    } catch { showToast("Yuklanmadi", 'error'); }
    setLoading(false);
  }, [migStatusF, countryF, probF, search]);

  useEffect(() => { load(); }, [load]);

  const createRef = async (data) => {
    try { await migrantsAPI.referrals.create(data); showToast("Yo'naltirish yaratildi"); setRefFormOpen(false); load(); }
    catch { showToast("Xatolik", 'error'); }
  };
  const deleteRef = async () => {
    try { await migrantsAPI.referrals.delete(deleteRefId); showToast("O'chirildi"); setDeleteRefId(null); load(); }
    catch { showToast("Xatolik", 'error'); }
  };

  const countries = Object.entries(stats.countries || {}).sort((a, b) => b[1] - a[1]);
  const maxCountry = countries[0]?.[1] || 1;
  const probData = stats.return_probability || {};

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* ===== HERO ===== */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #3b82f6 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -20, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: -30, left: 100, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 50, height: 50, borderRadius: 16, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>✈️</div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0 }}>Migrantlar bo'limi</h2>
              <p style={{ fontSize: 13, color: '#93c5fd', margin: 0 }}>Ketgan/kelgan yoshlar hisobi, qaytish tahlili va ish bilan ta'minlash</p>
            </div>
          </div>
          {/* Stat pills */}
          <div className="mig-hero-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {[
              { l: 'Jami migrantlar', v: stats.total_migrants || 0, i: '✈️', bg: 'rgba(255,255,255,0.12)' },
              { l: 'Chetda / Ketgan', v: `${stats.abroad || 0} / ${stats.gone || 0}`, i: '🌍', bg: 'rgba(239,68,68,0.2)' },
              { l: 'Qaytgan', v: stats.returned || 0, i: '🏠', bg: 'rgba(34,197,94,0.2)' },
              { l: 'Ishga joylashdi', v: stats.referrals_employed || 0, i: '💼', bg: 'rgba(59,130,246,0.2)' },
            ].map(s => (
              <div key={s.l} style={{ padding: '14px 16px', borderRadius: 14, background: s.bg, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>{s.i}</span>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s.v}</div>
                  <div style={{ fontSize: 10, color: '#93c5fd', fontWeight: 500, marginTop: 2 }}>{s.l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== TABS ===== */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 22, background: '#f1f5f9', borderRadius: 14, padding: 4, overflowX: 'auto' }}>
        {[
          { id: 'overview', label: "Yoshlar hisobi", icon: 'users' },
          { id: 'analysis', label: 'Qaytish tahlili', icon: 'trend' },
          { id: 'referrals', label: "Ish bilan ta'minlash", icon: 'briefcase' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
            background: tab === t.id ? '#fff' : 'transparent', boxShadow: tab === t.id ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
            color: tab === t.id ? '#1e40af' : 'var(--text-muted)',
            border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}>
            <Icon name={t.icon} size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* ===== TAB: OVERVIEW ===== */}
      {tab === 'overview' && (
        <>
          {/* ADMIN: faqat hudud jadval */}
          {isAdmin && (stats.tuman_breakdown || stats.mfy_breakdown) ? (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>🏢</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                  {user?.role === 'viloyat_admin' ? 'Tumanlar' : 'MFYlar'} bo'yicha migrant hisoboti
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
                  {(stats.tuman_breakdown || stats.mfy_breakdown || []).length} ta {user?.role === 'viloyat_admin' ? 'tuman' : 'MFY'}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                  <thead>
                    <tr style={{ background: '#fafbff' }}>
                      {['#', user?.role === 'viloyat_admin' ? 'Tuman' : 'MFY', 'Jami', 'Erkak', 'Ayol', 'Ketgan', 'Chetda', 'Qaytgan', 'Shartnomali', "Ro'yxatda", 'Qaytish: Past', "O'rta", 'Yuqori'].map(h => (
                        <th key={h} style={{ padding: '12px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '2px solid #e2e8f0', textAlign: h === '#' || h === 'Tuman' || h === 'MFY' ? 'left' : 'center', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.tuman_breakdown || stats.mfy_breakdown || []).map((row, i) => {
                      const name = row[user?.role === 'viloyat_admin' ? 'tuman' : 'mfy'] || "Noma'lum";
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <td style={{ padding: '11px 10px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                          <td style={{ padding: '11px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{name}</td>
                          <td style={{ padding: '11px 10px', textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{row.jami}</td>
                          <td style={{ textAlign: 'center' }}><span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#2563eb' }}>{row.erkak}</span></td>
                          <td style={{ textAlign: 'center' }}><span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fce7f3', color: '#db2777' }}>{row.ayol}</span></td>
                          <td style={{ textAlign: 'center' }}>{row.ketgan > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fef2f2', color: '#dc2626' }}>{row.ketgan}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center' }}>{row.chetda > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#ca8a04' }}>{row.chetda}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center' }}>{row.qaytgan > 0 ? <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#16a34a' }}>{row.qaytgan}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center', fontSize: 12, color: '#16a34a', fontWeight: 600 }}>{row.shartnomali || '—'}</td>
                          <td style={{ textAlign: 'center', fontSize: 12, color: '#2563eb', fontWeight: 600 }}>{row.royxatda || '—'}</td>
                          <td style={{ textAlign: 'center' }}>{row.qaytish_past > 0 ? <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 13 }}>{row.qaytish_past}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center' }}>{row.qaytish_orta > 0 ? <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13 }}>{row.qaytish_orta}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ textAlign: 'center' }}>{row.qaytish_yuqori > 0 ? <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 13 }}>{row.qaytish_yuqori}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ padding: '12px 10px' }} />
                      <td style={{ padding: '12px 10px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>JAMI</td>
                      {(() => {
                        const bd = stats.tuman_breakdown || stats.mfy_breakdown || [];
                        const sum = (k) => bd.reduce((s, r) => s + (r[k] || 0), 0);
                        return (
                          <>
                            <td style={{ textAlign: 'center', fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{sum('jami')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#2563eb' }}>{sum('erkak')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#db2777' }}>{sum('ayol')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>{sum('ketgan')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#ca8a04' }}>{sum('chetda')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{sum('qaytgan')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{sum('shartnomali')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#2563eb' }}>{sum('royxatda')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{sum('qaytish_past')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#f59e0b' }}>{sum('qaytish_orta')}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#22c55e' }}>{sum('qaytish_yuqori')}</td>
                          </>
                        );
                      })()}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <>
              {/* MFY: filters + cards */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                <Pill active={!migStatusF} onClick={() => setMigStatusF('')} label="Barchasi" count={stats.total_migrants || 0} />
                {MIG_STATUSES.map(s => (
                  <Pill key={s.value} active={migStatusF === s.value} onClick={() => setMigStatusF(s.value)}
                    color={s.color} bg={s.bg} label={s.label} icon={s.icon}
                    count={s.value === 'ketgan' ? stats.gone : s.value === 'chetda' ? stats.abroad : stats.returned || 0} />
                ))}
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 300 }}>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Qidirish..."
                    style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', fontSize: 13, fontFamily: 'inherit', color: 'var(--text)' }} />
                  <div style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}><Icon name="search" size={15} /></div>
                </div>
                {countries.length > 0 && (
                  <select value={countryF} onChange={e => setCountryF(e.target.value)}
                    style={{ padding: '11px 14px', borderRadius: 10, border: '1.5px solid var(--border)', background: '#fff', fontSize: 13, fontFamily: 'inherit' }}>
                    <option value="">Barcha davlatlar</option>
                    {countries.map(([c]) => <option key={c} value={c}>{getFlag(c)} {c}</option>)}
                  </select>
                )}
              </div>

              {loading ? <Loader /> : migrants.length === 0 ? (
                <Empty icon="✈️" title="Migrant yoshlar topilmadi" desc="Yoshlar reestriga migratsiya ma'lumotlarini kiriting" />
              ) : (
                <div className="mig-list" style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
                  {migrants.map(m => <MigrantCard key={m.id} data={m} />)}
                </div>
              )}
            </>
          )}

          <style>{`
            .mig-list { grid-template-columns: 1fr 1fr; }
            @media (max-width: 900px) { .mig-list { grid-template-columns: 1fr; } }
            .mig-hero-stats { grid-template-columns: repeat(4,1fr); }
            @media (max-width: 900px) { .mig-hero-stats { grid-template-columns: repeat(2,1fr); } }
            @media (max-width: 480px) { .mig-hero-stats { grid-template-columns: 1fr; } }
          `}</style>
        </>
      )}

      {/* ===== TAB: ANALYSIS ===== */}
      {tab === 'analysis' && (
        <div className="analysis-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Country breakdown */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', padding: '20px 24px' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              🌍 Davlatlar bo'yicha taqsimot
            </h3>
            {countries.length === 0 ? <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>Ma'lumot yo'q</p> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {countries.map(([country, count]) => (
                  <div key={country}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{getFlag(country)} {country}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{count} nafar</span>
                    </div>
                    <div style={{ height: 10, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 5, width: `${(count / maxCountry) * 100}%`, background: 'linear-gradient(90deg, #3b82f6, #6366f1)', transition: 'width .5s' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Return probability */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--border)', padding: '20px 24px' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              📊 Qaytish ehtimoli tahlili
            </h3>
            {Object.keys(probData).length === 0 ? <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>Ma'lumot yo'q</p> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {PROB_LEVELS.map(p => {
                  const count = probData[p.value] || 0;
                  const total = Object.values(probData).reduce((a, b) => a + b, 0) || 1;
                  return (
                    <div key={p.value} style={{ textAlign: 'center' }}>
                      <div style={{ position: 'relative', width: 90, height: 90, margin: '0 auto 8px', borderRadius: '50%' }}>
                        <svg width="90" height="90" style={{ transform: 'rotate(-90deg)' }}>
                          <circle cx="45" cy="45" r="38" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                          <circle cx="45" cy="45" r="38" fill="none" stroke={p.color} strokeWidth="8"
                            strokeDasharray={`${(count / total) * 239} 239`} strokeLinecap="round" />
                        </svg>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: p.color }}>{count}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: p.color }}>{p.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>qaytish ehtimoli</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Contract & Registration */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, padding: '12px', borderRadius: 10, background: '#eff6ff', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#3b82f6' }}>{stats.with_contract || 0}</div>
                  <div style={{ fontSize: 10, color: '#3b82f6', fontWeight: 500 }}>Shartnomali</div>
                </div>
                <div style={{ flex: 1, padding: '12px', borderRadius: 10, background: '#f0fdf4', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#22c55e' }}>{stats.registered || 0}</div>
                  <div style={{ fontSize: 10, color: '#22c55e', fontWeight: 500 }}>Ro'yxatdan o'tgan</div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary insight card */}
          <div style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #fefce8, #fef9c3)', borderRadius: 16, border: '1.5px solid #fde68a', padding: '20px 24px' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              💡 Xulosa va tavsiyalar
            </h3>
            <div className="insight-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              <InsightItem
                emoji="🔴" title="E'tibor kerak"
                text={`${probData['past'] || 0} nafar yoshning qaytish ehtimoli past. Ular bilan aloqa o'rnatish va oilalariga yordam berish kerak.`} />
              <InsightItem
                emoji="🟡" title="Kuzatuvda"
                text={`${probData["o'rta"] || 0} nafar o'rtacha ehtimollik. Ish bilan ta'minlash takliflari tayyorlash maqsadga muvofiq.`} />
              <InsightItem
                emoji="🟢" title="Ijobiy tendensiya"
                text={`${stats.returned || 0} nafar yosh qaytgan. ${stats.referrals_employed || 0} nafari ishga joylashtirildi.`} />
            </div>
          </div>

          <style>{`
            .analysis-grid { grid-template-columns: 1fr 1fr; }
            .insight-grid { grid-template-columns: repeat(3, 1fr); }
            @media (max-width: 900px) { .analysis-grid { grid-template-columns: 1fr; } .insight-grid { grid-template-columns: 1fr; } }
          `}</style>
        </div>
      )}

      {/* ===== TAB: REFERRALS ===== */}
      {tab === 'referrals' && (
        <>
          {/* Referral stats */}
          <div className="ref-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            <MiniStat icon="briefcase" label="Jami yo'naltirishlar" value={stats.referrals_total || 0} color="#3b82f6" />
            <MiniStat icon="clock" label="Jarayonda" value={stats.referrals_in_progress || 0} color="#f59e0b" />
            <MiniStat icon="checkCircle" label="Ishga joylashdi" value={stats.referrals_employed || 0} color="#22c55e" />
            <MiniStat icon="zap" label="Yangi" value={stats.referrals_new || 0} color="#6366f1" />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button onClick={() => setRefFormOpen(true)} icon="plus">Yo'naltirish yaratish</Button>
          </div>

          {loading ? <Loader /> : referrals.length === 0 ? (
            <Empty icon="💼" title="Hali yo'naltirish yo'q" desc="Qaytgan migrant yoshlarni ish bilan ta'minlashga yo'naltiring" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {referrals.map(r => <ReferralCard key={r.id} data={r} onView={async () => {
                try { const res = await migrantsAPI.referrals.get(r.id); setViewRef(res.data); } catch {}
              }} onDelete={() => setDeleteRefId(r.id)} />)}
            </div>
          )}

          <style>{`
            .ref-stats { grid-template-columns: repeat(4, 1fr); }
            @media (max-width: 900px) { .ref-stats { grid-template-columns: repeat(2, 1fr); } }
            @media (max-width: 480px) { .ref-stats { grid-template-columns: 1fr; } }
          `}</style>
        </>
      )}

      {/* ===== MODALS ===== */}
      <Modal open={refFormOpen} onClose={() => setRefFormOpen(false)} title="Ish bilan ta'minlashga yo'naltirish" width={600}>
        <ReferralForm onSave={createRef} onClose={() => setRefFormOpen(false)} migrants={migrants} />
      </Modal>
      <Modal open={!!viewRef} onClose={() => setViewRef(null)} title="Yo'naltirish tafsilotlari" width={560}>
        {viewRef && <ReferralDetail data={viewRef} />}
      </Modal>
      <Modal open={!!deleteRefId} onClose={() => setDeleteRefId(null)} title="O'chirishni tasdiqlang">
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>Ushbu yo'naltirishni o'chirishni xohlaysizmi?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteRefId(null)}>Bekor qilish</Button>
          <Button variant="danger" onClick={deleteRef}>O'chirish</Button>
        </div>
      </Modal>
    </div>
  );
}

// ===== Shared components =====
function Pill({ active, onClick, label, count, color, bg, icon }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 20,
      border: active ? `2px solid ${color || 'var(--accent)'}` : '2px solid var(--border)',
      background: active ? (bg || 'var(--accent-light)') : '#fff',
      color: active ? (color || 'var(--accent)') : 'var(--text-muted)',
      fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
    }}>
      {icon && <Icon name={icon} size={14} />} {label}
      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: active ? (color || 'var(--accent)') : '#e2e8f0', color: active ? '#fff' : 'var(--text-dim)' }}>{count}</span>
    </button>
  );
}

function MiniStat({ icon, label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid var(--border)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: color + '12', display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        <Icon name={icon} size={18} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 500, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

function InsightItem({ emoji, title, text }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.7)' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{emoji} {title}</div>
      <div style={{ fontSize: 12, color: '#78716c', lineHeight: 1.5 }}>{text}</div>
    </div>
  );
}

function Loader() { return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-dim)' }}>Yuklanmoqda...</div>; }
function Empty({ icon, title, desc }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 20, border: '2px dashed var(--border)' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{title}</h3>
      <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>{desc}</p>
    </div>
  );
}

// ===== Migrant Card =====
function MigrantCard({ data: m }) {
  const ms = getMS(m.mig_status);
  const prob = PROB_LEVELS.find(p => p.value === m.mig_return_probability);
  const days = m.mig_departure_date ? Math.floor((new Date() - new Date(m.mig_departure_date)) / 86400000) : null;

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '16px 20px',
      border: `1.5px solid var(--border)`, borderLeft: `4px solid ${ms.color}`, transition: 'all .2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(59,130,246,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          {/* Name */}
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{m.full_name}</div>
          {/* Badges */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: ms.bg, color: ms.color }}>
              <Icon name={ms.icon} size={12} /> {ms.label}
            </span>
            {m.mig_country && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#eff6ff', color: '#1e40af' }}>
                {getFlag(m.mig_country)} {m.mig_country}
              </span>
            )}
            {prob && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: prob.color + '15', color: prob.color }}>
                Qaytish: {prob.label}
              </span>
            )}
          </div>
          {/* Details */}
          <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--text-dim)', flexWrap: 'wrap' }}>
            <span>{m.age} yosh • {m.gender === 'male' ? 'Erkak' : 'Ayol'}</span>
            {m.phone && <span><Icon name="phone" size={10} style={{ verticalAlign: 'middle' }} /> {m.phone}</span>}
            {m.mig_departure_date && <span>Ketgan: {m.mig_departure_date}</span>}
            {m.mig_return_date && <span style={{ color: '#22c55e', fontWeight: 600 }}>Qaytgan: {m.mig_return_date}</span>}
            {days && m.mig_status !== 'qaytgan' && <span style={{ color: '#f59e0b' }}>{days} kun</span>}
          </div>
        </div>
        {/* Contract/Registration badges */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, alignItems: 'flex-end' }}>
          {m.mig_contract && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#f0fdf4', color: '#16a34a' }}>📄 Shartnoma</span>
          )}
          {m.mig_registered && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#eff6ff', color: '#2563eb' }}>✓ Ro'yxatda</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Referral Card =====
function ReferralCard({ data: r, onView, onDelete }) {
  const rt = getRT(r.referral_type);
  const rs = getRS(r.status);
  return (
    <div onClick={onView} style={{
      background: '#fff', borderRadius: 14, padding: '16px 20px', cursor: 'pointer',
      border: `1.5px solid var(--border)`, borderLeft: `4px solid ${rt.color}`, transition: 'all .2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(59,130,246,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: rt.color + '12', color: rt.color }}>
              <Icon name={rt.icon} size={12} /> {rt.label}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: rs.bg, color: rs.color }}>{rs.label}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>{r.youth_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{r.organization}{r.position ? ` — ${r.position}` : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
          <button onClick={onDelete} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}>
            <Icon name="trash" size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Referral Form =====
function ReferralForm({ onSave, onClose, migrants }) {
  const [youthList, setYouthList] = useState([]);
  const [form, setForm] = useState({ youth: '', referral_type: 'ish', organization: '', position: '', note: '' });
  useEffect(() => { youthAPI.mini().then(r => setYouthList(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Show returned migrants first
  const returned = migrants.filter(m => m.mig_status === 'qaytgan');
  const returnedIds = new Set(returned.map(m => m.id));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Yosh <span style={{ color: 'var(--red)' }}>*</span></label>
        <select value={form.youth} onChange={e => set('youth', e.target.value)}
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit' }}>
          <option value="">Yoshni tanlang...</option>
          {returned.length > 0 && <optgroup label="🏠 Qaytgan migrantlar">
            {returned.map(y => <option key={y.id} value={y.id}>{y.full_name} ({y.age} yosh, {y.mig_country})</option>)}
          </optgroup>}
          <optgroup label="Barcha yoshlar">
            {youthList.filter(y => !returnedIds.has(y.id)).map(y => <option key={y.id} value={y.id}>{y.full_name} ({y.age} yosh)</option>)}
          </optgroup>
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Yo'naltirish turi</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {REF_TYPES.map(t => (
            <button key={t.value} onClick={() => set('referral_type', t.value)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 12, flex: 1, minWidth: 120,
              border: form.referral_type === t.value ? `2.5px solid ${t.color}` : '2px solid var(--border)',
              background: form.referral_type === t.value ? t.color + '10' : '#fff',
              color: form.referral_type === t.value ? t.color : 'var(--text-muted)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', justifyContent: 'center',
            }}>
              <Icon name={t.icon} size={15} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Tashkilot nomi" value={form.organization} onChange={v => set('organization', v)} placeholder="Tashkilot..." />
        <Input label="Lavozim / Kurs nomi" value={form.position} onChange={v => set('position', v)} placeholder="Lavozim..." />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Izoh</label>
        <textarea value={form.note} onChange={e => set('note', e.target.value)} rows={2} placeholder="Qo'shimcha ma'lumot..."
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12, border: '2px solid var(--border)', background: 'var(--bg)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onClose}>Bekor qilish</Button>
        <Button onClick={() => { if (form.youth) onSave(form); }} disabled={!form.youth} icon="briefcase">Yo'naltirish</Button>
      </div>
    </div>
  );
}

// ===== Referral Detail =====
function ReferralDetail({ data: r }) {
  const rt = getRT(r.referral_type);
  const rs = getRS(r.status);
  const Row = ({ icon, label, value }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', flexShrink: 0 }}><Icon name={icon} size={14} /></div>
      <div><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{label}</div><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 1 }}>{value || '—'}</div></div>
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: rt.color + '12', color: rt.color }}>
          <Icon name={rt.icon} size={14} /> {rt.label}
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: rs.bg, color: rs.color }}>{rs.label}</span>
      </div>
      <Row icon="users" label="Yosh" value={`${r.youth_name}${r.youth_phone ? ' • ' + r.youth_phone : ''}`} />
      <Row icon="building" label="Tashkilot" value={r.organization} />
      <Row icon="briefcase" label="Lavozim / Kurs" value={r.position} />
      {r.note && <Row icon="info" label="Izoh" value={r.note} />}
      {r.result && <Row icon="checkCircle" label="Natija" value={r.result} />}
      <Row icon="clock" label="Yaratilgan" value={r.created_at?.slice(0, 10)} />
    </div>
  );
}
